import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  ImageBackground,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { genderedArticle } from '@/lib/format';
import { setPendingInviteAgreement } from '@/lib/invite';

const wallpaper = require('../../../assets/wallpaper.jpg');
const logo = require('../../../assets/logo.png');

const GOLD = '#C9A843';
const CARD_BG = 'rgba(13, 27, 42, 0.88)';
const CARD_BORDER = 'rgba(201, 168, 67, 0.22)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.58)';

type PreviewData = {
  id: string;
  title: string;
  description?: string | null;
  creator_name: string;
  creator_avatar?: string | null;
};

export default function InviteScreen() {
  const { t } = useTranslation();
  const { agreement } = useLocalSearchParams<{ agreement?: string }>();
  const { session } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';

  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agreement) { setLoading(false); return; }
    void fetch(`${supabaseUrl}/functions/v1/agreement-preview/${agreement}`)
      .then((r) => r.json())
      .then(async (data) => {
        if (data.error) {
          setError(t('invite.notFound'));
        } else {
          await setPendingInviteAgreement(data.id);
          if (session) {
            // Ο χρήστης είναι ήδη συνδεδεμένος → πηγαίνε κατευθείαν στη συμφωνία
            router.replace({ pathname: '/(app)/agreements/[id]', params: { id: data.id as string } });
            return;
          }
          setPreview(data as PreviewData);
        }
      })
      .catch(() => setError(t('invite.loadError')))
      .finally(() => setLoading(false));
  }, [agreement, supabaseUrl, session, router]);

  function goRegister() {
    router.replace('/(auth)/register');
  }

  function goLogin() {
    router.replace('/(auth)/login');
  }

  return (
    <ImageBackground source={wallpaper} style={styles.flex} resizeMode="cover">
      <View style={styles.overlay} />
      <View
        style={[
          styles.container,
          { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.lg },
        ]}
      >
        <View style={styles.logoWrap}>
          <Image source={logo} style={styles.logo} resizeMode="contain" />
        </View>

        {loading ? (
          <View style={[styles.card, styles.center]}>
            <ActivityIndicator color={GOLD} size="large" />
          </View>
        ) : error ? (
          <View style={styles.card}>
            <Text style={styles.errorText}>{error}</Text>
            <Button title={t('auth.action.login')} onPress={goLogin} />
            <Button title={t('auth.action.register')} variant="secondary" onPress={goRegister} />
          </View>
        ) : preview ? (
          <View style={styles.card}>
            <View style={styles.inviteHeader}>
              <Ionicons name="document-text-outline" size={28} color={GOLD} />
              <Text style={styles.inviteLabel}>{t('invite.agreement_header')}</Text>
            </View>

            <View style={styles.creatorRow}>
              {preview.creator_avatar ? (
                <Image
                  source={{ uri: preview.creator_avatar }}
                  style={styles.avatar}
                />
              ) : (
                <View style={styles.avatarFallback}>
                  <Text style={styles.avatarInitial}>
                    {preview.creator_name.charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <Text style={styles.creatorName}>{preview.creator_name}</Text>
            </View>

            <View style={styles.agreementCard}>
              <Text style={styles.agreementTitle}>{preview.title}</Text>
              {!!preview.description && (
                <Text style={styles.agreementDesc} numberOfLines={3}>{preview.description}</Text>
              )}
            </View>

            <Text style={styles.inviteText}>
              {genderedArticle(preview.creator_name)} <Text style={styles.boldGold}>{preview.creator_name}</Text> {t('invite.agreement_inviteText')}
            </Text>

            <Button title={t('invite.action_registerAndSign')} icon="create-outline" onPress={goRegister} />
            <View style={styles.divider}>
              <Text style={styles.dividerText}>{t('invite.have_account')}</Text>
            </View>
            <Button title={t('auth.action.login')} variant="secondary" onPress={goLogin} />

            <Text style={styles.tagline}>
              {t('invite.tagline')}
            </Text>
          </View>
        ) : (
          <View style={styles.card}>
            <Text style={styles.errorText}>{t('invite.notFound')}</Text>
            <Button title={t('auth.action.login')} onPress={goLogin} />
            <Button title={t('auth.action.register')} variant="secondary" onPress={goRegister} />
          </View>
        )}
      </View>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  overlay: {
    ...require('react-native').StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13,27,42,0.68)',
  },
  container: {
    // Σε μεγάλες οθόνες η φόρμα μένει κεντραρισμένη σε αναγνώσιμο πλάτος
    // αντί να τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
    width: '100%',
    maxWidth: contentMaxWidth.form,
    alignSelf: 'center',
    flex: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.xl,
  },
  logoWrap: { alignItems: 'center' },
  logo: { width: 120, height: 120 },
  card: {
    backgroundColor: CARD_BG,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    padding: spacing.lg,
    gap: spacing.md,
  },
  center: { alignItems: 'center', justifyContent: 'center', minHeight: 180 },
  inviteHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  inviteLabel: { color: GOLD, fontSize: fontSize.lg, fontWeight: '700' },
  creatorRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  avatar: { width: 44, height: 44, borderRadius: 22 },
  avatarFallback: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: 'rgba(201,168,67,0.2)',
    alignItems: 'center', justifyContent: 'center',
  },
  avatarInitial: { color: GOLD, fontSize: fontSize.xl, fontWeight: '700' },
  creatorName: { color: TEXT_WHITE, fontSize: fontSize.md, fontWeight: '600' },
  agreementCard: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: radius.md,
    padding: spacing.md,
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
  },
  agreementTitle: { color: TEXT_WHITE, fontSize: fontSize.md, fontWeight: '700' },
  agreementDesc: { color: TEXT_MUTED, fontSize: fontSize.sm, lineHeight: 19 },
  inviteText: { color: TEXT_MUTED, fontSize: fontSize.md, lineHeight: 22, textAlign: 'center' },
  boldGold: { color: GOLD, fontWeight: '700' },
  divider: { alignItems: 'center' },
  dividerText: { color: TEXT_MUTED, fontSize: fontSize.sm },
  tagline: {
    color: GOLD,
    fontSize: fontSize.sm,
    textAlign: 'center',
    lineHeight: 18,
    fontStyle: 'italic',
  },
  errorText: { color: '#F87171', fontSize: fontSize.md, textAlign: 'center' },
});
